"use client";

/**
 * ConnectivityCapsule — the connection-truth transport capsule.
 *
 * One fixed top-center capsule on every (app) surface when the health rail
 * says the client can't (fully) hear the server: quiet gray "You're offline"
 * when down, "Connection is slow" when degraded. Calm vocabulary — a blip is
 * never an alarm — and strictly connectivity language, so a network outage
 * can no longer masquerade as sandbox-sleep ("Asleep / Type to wake it up").
 *
 * Suppressed during a deploy window: the UpdatingCapsule's calm gold owns
 * that story, and stacking two capsules would say two things at once.
 * Mounted once in AppShell, beside UpdatingCapsule.
 */

import { useConnectionStore } from "@/stores/connectionStore";
import { useConnectionHealthStore } from "@/stores/connectionHealthStore";

export function ConnectivityCapsule() {
  const health = useConnectionHealthStore((s) => s.health);
  const browserOffline = useConnectionHealthStore((s) => s.offline);
  const inDeployWindow = useConnectionStore(
    (s) => !!s.deployUpdating || s.deployUpdatedTick
  );
  // "reconnecting…" is a claim about a re-dial actually being in flight, not a
  // decoration on every down. A tab that came back to a dead link says what it
  // knows and nothing more.
  const reconnecting = useConnectionStore((s) => s.isSocketReconnecting);

  if (health === "healthy" || inDeployWindow) return null;

  const down = health === "down";

  return (
    <div
      className="fixed top-3 left-1/2 -translate-x-1/2 w-max max-w-[calc(100vw-1rem)] z-[84] pointer-events-none"
      data-testid="connectivity-capsule"
      data-health={health}
      role="status"
      aria-live="polite"
    >
      <div className="inline-flex items-center gap-2 rounded-full border border-border-default bg-bg-elevated px-4 py-1.5 text-3.5 font-medium text-fg-default shadow-connectivity-capsule">
        <span
          className="rounded-full w-[6px] h-[6px]"
          style={{
            backgroundColor: down ? "var(--color-gray)" : "var(--color-amber)",
          }}
          aria-hidden
        />
        <span>
          {down
            ? browserOffline
              ? "You're offline"
              : "Can't reach Vestra"
            : "Connection is slow"}
        </span>
        {down && reconnecting && (
          <span className="font-normal text-fg-subtle">reconnecting…</span>
        )}
      </div>
    </div>
  );
}
